import { useEffect } from "react";
import { z } from "zod";

import { ColocaDateRangeField } from "@/shared/components/forms/ColocaDateRangeField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";

import type { OtcHistoryFilters } from "../../utils/cryptoSwapFilters";
import { CryptoSwapOrganizationPicker } from "../shared/CryptoSwapOrganizationPicker";

/** The range picker needs a React Hook Form context like every Design System field. */
const rangeSchema = z.object({ from: z.string(), to: z.string() });

export interface CryptoSwapOrdersFiltersProps {
  filters: OtcHistoryFilters;
  onRangeChange: (from: string, to: string) => void;
  /** Staff only; absent for members, who are pinned to their own organization. */
  onOrganizationChange?: (organizationId: string) => void;
}

export function CryptoSwapOrdersFilters({
  filters,
  onRangeChange,
  onOrganizationChange,
}: CryptoSwapOrdersFiltersProps) {
  const { t } = useTranslations("cryptoSwaps");
  const form = useZodForm(rangeSchema, { from: filters.from, to: filters.to });

  useEffect(() => {
    const subscription = form.watch((values) => onRangeChange(values.from ?? "", values.to ?? ""));
    return () => subscription.unsubscribe();
  }, [form, onRangeChange]);

  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
      <ColocaForm form={form} onSubmit={() => undefined} className="w-full space-y-0 lg:w-72">
        <ColocaDateRangeField
          nameFrom="from"
          nameTo="to"
          label={t("history_range_label")}
          placeholder={t("history_range_placeholder")}
          maxDate={new Date()}
        />
      </ColocaForm>

      {onOrganizationChange && (
        <div className="w-full lg:w-96">
          <CryptoSwapOrganizationPicker
            id="crypto-swap-history-organization"
            mode="scope"
            value={filters.organizationId || null}
            onValueChange={(organizationId) => onOrganizationChange(organizationId ?? "")}
          />
        </div>
      )}
    </div>
  );
}
